import { useEffect } from 'react'
import { X } from 'lucide-react'
import { CATEGORY_CONFIG, CATEGORY_ORDER } from '@/data/patchNoteCategories'

/**
 * Preview a single patch note as a regular user would see it in UpdateModal.
 * Admin-only surface — no DB writes, no seen-tracking, no fetch. Accepts the
 * note + its items and renders the same card layout UpdateModal uses.
 */
export default function PatchNotePreviewModal({ note, onClose }) {
  useEffect(() => {
    const onKey = e => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  if (!note) return null

  const items = note.patch_note_items || []

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Patch note preview"
    >
      <div
        className="bg-[var(--bg-elevated)] border border-[var(--glass-border)] rounded-2xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Preview banner — makes it obvious this is an admin preview */}
        <div className="bg-[var(--gold)]/10 border-b border-[var(--gold)]/30 px-6 py-2 flex items-center justify-between">
          <span className="text-xs font-semibold text-[var(--gold)] uppercase tracking-wider">
            Preview — user view
          </span>
          <span className="text-[10px] text-[var(--text-muted)]">
            {note.status === 'live' ? 'Live' : 'Draft'}
          </span>
        </div>

        {/* Header (matches UpdateModal) */}
        <div className="flex items-center justify-between p-6 pb-4 border-b border-[var(--glass-border)]">
          <div>
            <p className="text-[var(--gold)] text-xs font-semibold uppercase tracking-widest mb-1">
              What's New
            </p>
            <h2 className="font-heading text-xl font-bold text-[var(--text-primary)]">
              {note.title}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors p-1"
            aria-label="Close preview"
          >
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-6 space-y-8">
          <div>
            <div className="flex items-center gap-3 flex-wrap mb-4">
              <span className="bg-[var(--gold)]/20 text-[var(--gold)] text-xs font-bold px-3 py-1 rounded-full">
                v{note.version}
              </span>
              <span className="text-[var(--text-primary)] font-semibold">{note.title}</span>
            </div>

            {CATEGORY_ORDER.map(cat => {
              const cfg = CATEGORY_CONFIG[cat]
              const catItems = items
                .filter(i => i.category === cat)
                .sort((a, b) => a.sort_order - b.sort_order)
              if (!catItems.length) return null
              const Icon = cfg.icon
              return (
                <div key={cat} className="mb-4">
                  <div className={`flex items-center gap-2 mb-2 ${cfg.color}`}>
                    <Icon className="w-4 h-4" />
                    <span className="text-xs font-semibold uppercase tracking-wider">
                      {cfg.label}
                    </span>
                  </div>
                  <ul className="space-y-1.5">
                    {catItems.map(item => (
                      <li
                        key={item.id}
                        className="flex items-start gap-2 text-sm text-[var(--text-muted)]"
                      >
                        <span className="text-[var(--gold)] mt-1.5 text-xs leading-none">◆</span>
                        {item.description}
                      </li>
                    ))}
                  </ul>
                </div>
              )
            })}

            {items.length === 0 && (
              <p className="text-sm text-[var(--text-muted)] italic">No items yet.</p>
            )}
          </div>
        </div>

        {/* Footer (matches UpdateModal's shape — "Got it" + Full changelog) */}
        <div className="p-6 pt-4 border-t border-[var(--glass-border)] flex items-center justify-between gap-4">
          <span className="text-xs text-[var(--text-muted-soft)] italic">
            This is how users will see the modal.
          </span>
          <button
            onClick={onClose}
            className="bg-[var(--gold)] text-[var(--text-on-gold)] font-bold px-6 py-2.5 rounded-lg hover:opacity-90 transition-all text-sm"
          >
            Close preview
          </button>
        </div>
      </div>
    </div>
  )
}
